<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50" @click.self="closeModal">
    <div class="bg-white/15 border border-white/20 rounded-xl shadow-xl w-11/12 max-w-4xl max-h-[90vh] flex flex-col">

      <!-- Header -->
      <div class="flex justify-between items-center px-6 py-3 border-b border-white/20 bg-ui-bg">
        <h3 class="text-base font-semibold text-ui-active">String Builder</h3>
        <button
          class="text-gray-400 hover:text-white hover:bg-white/15 w-7 h-7 rounded flex items-center justify-center transition-colors"
          @click="closeModal"
        >
          ✕
        </button>
      </div>

      <!-- Text Editor -->
      <div class="px-6 py-3 border-b border-white/20 bg-ui-bg">
        <label class="block text-gray-400 font-medium mb-1.5 text-xs uppercase tracking-wide">Text</label>
        <input
          ref="textEditor"
          type="text"
          v-model="editableText"
          @click="updateCursorPosition"
          @keyup="updateCursorPosition"
          placeholder="Enter text, click icons or variables to insert..."
          class="w-full px-3 py-2 border border-white/20 rounded bg-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-ui-active font-mono text-sm"
        />
        <div class="mt-2 text-xs text-gray-400 flex items-center gap-2">
          Preview: <span class="openhasp-built-in-icons bg-white/15 px-2 py-1 rounded border border-white/20 text-white font-mono">{{ previewText }}</span>
        </div>
      </div>

      <!-- Tabs -->
      <div v-if="availableTabs.length > 1" class="flex border-b border-white/20 bg-ui-bg flex-shrink-0">
        <button
          v-if="availableTabs.includes('icons')"
          class="px-5 py-2.5 text-xs font-medium transition-colors"
          :class="activeTab === 'icons' ? 'text-white border-b-2 border-ui-active bg-white/15' : 'text-gray-500 hover:text-gray-300'"
          @click="activeTab = 'icons'"
        >Icons</button>
        <button
          v-if="availableTabs.includes('variables')"
          class="px-5 py-2.5 text-xs font-medium transition-colors"
          :class="activeTab === 'variables' ? 'text-white border-b-2 border-ui-active bg-white/15' : 'text-gray-500 hover:text-gray-300'"
          @click="activeTab = 'variables'"
        >Variables</button>
        <button
          v-if="availableTabs.includes('datetime')"
          class="px-5 py-2.5 text-xs font-medium transition-colors"
          :class="activeTab === 'datetime' ? 'text-white border-b-2 border-ui-active bg-white/15' : 'text-gray-500 hover:text-gray-300'"
          @click="activeTab = 'datetime'"
        >Date &amp; Time</button>
      </div>

      <!-- Icons Tab -->
      <template v-if="activeTab === 'icons'">
        <div class="px-6 py-3 border-b border-white/20 bg-ui-bg flex-shrink-0">
          <input
            type="text"
            v-model="searchQuery"
            @input="filterIcons"
            placeholder="Search icons… (e.g. home, settings, menu)"
            class="w-full px-3 py-2 border border-white/20 rounded bg-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-ui-active text-xs"
          />
          <div class="mt-1.5 text-xs text-gray-500">
            {{ filteredIcons.length }} OpenHASP built-in icons {{ searchQuery ? 'found' : 'available' }}
          </div>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4 bg-white/15">
          <div class="grid grid-cols-6 sm:grid-cols-7 md:grid-cols-8 lg:grid-cols-9 gap-2">
            <div
              v-for="iconName in displayedIcons"
              :key="iconName"
              class="flex flex-col items-center p-2 border border-white/20 bg-ui-bg rounded-lg cursor-pointer transition-all hover:border-ui-active hover:bg-white/15 hover:-translate-y-0.5"
              @click="selectIcon(iconName)"
              :title="iconName"
            >
              <div class="openhasp-built-in-icons text-2xl text-ui-active mb-1">{{ getIconChar(iconName) }}</div>
              <div class="text-xs text-gray-400 text-center break-words line-clamp-2 w-full">{{ iconName }}</div>
            </div>
          </div>
          <div v-if="filteredIcons.length > displayLimit" class="mt-4 text-center">
            <button
              @click="loadMore"
              class="px-4 py-1.5 bg-ui-active text-white rounded hover:opacity-90 transition-colors text-xs font-medium"
            >
              Load More ({{ filteredIcons.length - displayedIcons.length }} remaining)
            </button>
          </div>
        </div>
      </template>

      <!-- Variables Tab -->
      <template v-if="activeTab === 'variables'">
        <div class="flex-1 overflow-y-auto px-6 py-4 bg-white/15">
          <p v-if="resolvedVariables.length" class="text-gray-500 text-xs mb-3">Click a variable to insert it at the cursor position.</p>
          <p v-else class="text-gray-500 text-xs mb-3">No variables are available for this value.</p>
          <div class="flex flex-col gap-2">
            <div
              v-for="variable in resolvedVariables"
              :key="variable.token"
              class="flex items-center gap-4 p-2.5 border border-green-900/60 bg-ui-bg rounded-lg cursor-pointer transition-all hover:border-green-600 hover:bg-white/15"
              @click="insertVariable(variable.token)"
            >
              <span class="text-green-400 text-xs font-bold w-28 shrink-0"
                :class="{ 'font-mono': !variable.label }">{{ variable.label || variable.token }}</span>
              <span class="text-gray-300 text-xs">{{ variable.description }}</span>
              <span v-if="variable.label" class="font-mono text-gray-500 text-xs ml-auto shrink-0">{{ variable.token }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Date & Time Tab -->
      <template v-if="activeTab === 'datetime'">
        <div class="flex-1 overflow-y-auto px-6 py-4 bg-white/15">
          <p class="text-gray-500 text-xs mb-3">Click a specifier to insert it. Combine freely, e.g. <span class="font-mono text-yellow-300">%H:%M</span> or <span class="font-mono text-yellow-300">%a %d %b</span>.</p>
          <div class="flex flex-col gap-4">
            <div v-for="group in strftimeGroups" :key="group.label">
              <h4 class="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">{{ group.label }}</h4>
              <div class="flex flex-col gap-1.5">
                <div
                  v-for="spec in group.specifiers"
                  :key="spec.token"
                  class="flex items-center gap-4 p-2.5 border border-yellow-900/60 bg-ui-bg rounded-lg cursor-pointer transition-all hover:border-yellow-600 hover:bg-white/15"
                  @click="insertVariable(spec.token)"
                >
                  <span class="font-mono text-yellow-300 text-xs font-bold w-10 shrink-0">{{ spec.token }}</span>
                  <span class="font-mono text-slate-500 text-xs w-36 shrink-0">e.g. {{ spec.example }}</span>
                  <span class="text-gray-300 text-xs">{{ spec.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Footer -->
      <div class="px-6 py-3 border-t border-white/20 bg-ui-bg flex justify-between items-center gap-4 flex-shrink-0">
        <div class="flex-1">
          <div v-if="activeTab === 'icons' && selectedIconName" class="text-ui-active mb-1 text-xs">
            Selected: <strong>{{ selectedIconName }}</strong>
          </div>
          <div class="text-gray-500 text-xs">
            <template v-if="activeTab === 'icons'">All 153 icons work on all OpenHASP devices without custom fonts.</template>
            <template v-else-if="activeTab === 'variables'">{{ variablesHint }}</template>
            <template v-else>strftime specifiers are resolved at runtime on the plate.</template>
          </div>
        </div>
        <div class="flex gap-2">
          <form-button @click="closeModal" class="w-24">Cancel</form-button>
          <form-button @click="applyText" color="success" class="w-24">Apply</form-button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { getBuiltInIconNames, getBuiltInIconCharacter } from '../utils/openHaspBuiltinIcons';
import { OPENHASP_RUNTIME_VARIABLES } from '../utils/openHaspRuntimeVariables';

export default {
  name: 'IconPicker',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    initialText: {
      type: String,
      default: ''
    },
    defaultTab: {
      type: String,
      default: 'icons'
    },
    availableTabs: {
      type: Array,
      default: () => ['icons', 'variables', 'datetime']
    },
    /**
     * The complete Variables list for this context, replacing the built-in
     * openHASP runtime placeholders outright — no hidden fallback, an empty
     * array really means "this value has no variables". Callers whose strings
     * are rendered by Home Assistant rather than by the plate pass their own
     * list here (see StringBuilderField.vue). Leave it unset to keep the
     * openHASP list, optionally extended via `extraVariables` below.
     *
     * Shape: { token, label?, description } — `token` is inserted at the
     * cursor, `label` names it in the list when the raw token isn't
     * self-explanatory.
     */
    variables: {
      type: Array,
      default: null
    },
    /** Appended to the openHASP runtime list. Ignored when `variables` is set. */
    extraVariables: {
      type: Array,
      default: () => []
    },
    previewValues: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'text-updated'],
  data() {
    return {
      // Also re-applied by the modelValue watcher on every open.
      activeTab: this.defaultTab,
      searchQuery: '',
      allIcons: [],
      filteredIcons: [],
      displayedIcons: [],
      displayLimit: 100,
      selectedIconName: null,
      editableText: '',
      cursorPosition: 0,
      strftimeGroups: [
        {
          label: 'Time',
          specifiers: [
            { token: '%H', example: '14',       description: 'Hour, 24h (00–23)' },
            { token: '%I', example: '02',        description: 'Hour, 12h (01–12)' },
            { token: '%M', example: '55',        description: 'Minute (00–59)' },
            { token: '%S', example: '02',        description: 'Second (00–60)' },
            { token: '%p', example: 'PM',        description: 'AM / PM' },
            { token: '%X', example: '14:55:02',  description: 'Time representation' },
          ]
        },
        {
          label: 'Date',
          specifiers: [
            { token: '%d', example: '23',        description: 'Day of month, zero-padded (01–31)' },
            { token: '%m', example: '08',        description: 'Month as number (01–12)' },
            { token: '%Y', example: '2001',      description: 'Full year' },
            { token: '%y', example: '01',        description: 'Year, last two digits (00–99)' },
            { token: '%x', example: '08/23/01',  description: 'Date representation' },
          ]
        },
        {
          label: 'Weekday & Month names',
          specifiers: [
            { token: '%a', example: 'Thu',       description: 'Abbreviated weekday name' },
            { token: '%A', example: 'Thursday',  description: 'Full weekday name' },
            { token: '%b', example: 'Aug',       description: 'Abbreviated month name' },
            { token: '%B', example: 'August',    description: 'Full month name' },
            { token: '%w', example: '4',         description: 'Weekday as number, Sunday = 0 (0–6)' },
          ]
        },
        {
          label: 'Week & Day of year',
          specifiers: [
            { token: '%j', example: '235',       description: 'Day of year (001–366)' },
            { token: '%U', example: '33',        description: 'Week number, first Sunday (00–53)' },
            { token: '%W', example: '34',        description: 'Week number, first Monday (00–53)' },
          ]
        },
        {
          label: 'Other',
          specifiers: [
            { token: '%Z', example: 'CEST',      description: 'Timezone name or abbreviation' },
            { token: '%c', example: 'Thu Aug 23 14:55:02 2001', description: 'Full date and time' },
            { token: '%%', example: '%',         description: 'Literal % character' },
          ]
        }
      ]
    };
  },
  computed: {
    /**
     * An explicit `variables` list wins outright; otherwise the openHASP
     * runtime placeholders, plus whatever `extraVariables` adds. This is the
     * only place the Variables tab's contents are decided — nothing here
     * inspects which editor opened the builder.
     */
    resolvedVariables() {
      return this.variables ?? [...OPENHASP_RUNTIME_VARIABLES, ...this.extraVariables];
    },

    /**
     * The footer note under the Variables tab. Who resolves a variable depends
     * on the list, not on the builder: the openHASP placeholders are
     * substituted by the firmware, a caller-supplied list belongs to whatever
     * renders that caller's strings.
     */
    variablesHint() {
      if (!this.resolvedVariables.length) return 'This value takes plain text and icons.';
      return this.variables
        ? 'Variables are resolved where this value is rendered.'
        : 'Variables are resolved at runtime on the plate.';
    },
    previewText() {
      let preview = this.editableText;
      for (const [name, value] of Object.entries(this.previewValues)) {
        preview = preview.split(`{${name}}`).join(String(value));
      }
      return preview;
    },
    isOpen() {
      return this.modelValue;
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.editableText = this.initialText;
        this.activeTab = this.defaultTab;
        this.loadIcons();
        this.$nextTick(() => {
          const textInput = this.$refs.textEditor;
          if (textInput) {
            textInput.focus();
            this.cursorPosition = textInput.selectionStart || this.editableText.length;
          }
        });
      }
    },
    initialText(newVal) {
      this.editableText = newVal;
    }
  },
  methods: {
    async loadIcons() {
      this.allIcons = getBuiltInIconNames();
      this.filterIcons();
    },
    
    filterIcons() {
      let icons = this.allIcons;
      
      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        icons = icons.filter(name => name.includes(query));
      }
      
      this.filteredIcons = icons;
      this.displayLimit = 100;
      this.displayedIcons = this.filteredIcons.slice(0, this.displayLimit);
    },
    
    loadMore() {
      this.displayLimit += 100;
      this.displayedIcons = this.filteredIcons.slice(0, this.displayLimit);
    },
    
    getIconChar(iconName) {
      return getBuiltInIconCharacter(iconName);
    },
    
    selectIcon(iconName) {
      this.selectedIconName = iconName;
      
      // Always insert actual character (OpenHASP/LVGL style)
      // The properties panel will display it as \uXXXX for readability
      const iconText = getBuiltInIconCharacter(iconName);
      
      // Insert icon at cursor position
      const before = this.editableText.substring(0, this.cursorPosition);
      const after = this.editableText.substring(this.cursorPosition);
      this.editableText = before + iconText + after;
      this.cursorPosition = before.length + iconText.length;
      
      // Update cursor position in text input
      this.$nextTick(() => {
        const textInput = this.$refs.textEditor;
        if (textInput) {
          textInput.focus();
          textInput.setSelectionRange(this.cursorPosition, this.cursorPosition);
        }
      });
    },
    
    updateCursorPosition() {
      const textInput = this.$refs.textEditor;
      if (textInput) {
        this.cursorPosition = textInput.selectionStart || 0;
      }
    },
    
    applyText() {
      this.$emit('text-updated', this.editableText);
      this.closeModal();
    },
    
    insertVariable(token) {
      const before = this.editableText.substring(0, this.cursorPosition);
      const after = this.editableText.substring(this.cursorPosition);
      this.editableText = before + token + after;
      this.cursorPosition = before.length + token.length;

      this.$nextTick(() => {
        const textInput = this.$refs.textEditor;
        if (textInput) {
          textInput.focus();
          textInput.setSelectionRange(this.cursorPosition, this.cursorPosition);
        }
      });
    },

    closeModal() {
      this.$emit('update:modelValue', false);
      this.searchQuery = '';
      this.selectedIconName = null;
      this.activeTab = this.defaultTab;
    }
  }
};
</script>

<style scoped>
/* Ensure line-clamp works for icon names */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
